import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { request, setCurrentStation } from '@/api/client';

/**
 * Which station the operator is working in.
 *
 * A tenant can run several, and every screen in the panel is about one of them at a
 * time. The choice is remembered per browser so a reload does not drop somebody back
 * into a different station than the one they were last looking at, and it is validated
 * against what the API says they may reach: a remembered station they have since lost
 * access to falls back rather than producing a wall of refusals.
 */
export interface StationSummary {
  id: string;
  name: string;
  slug: string;
  organisation: { id: string; name: string };
  role: string | null;
}

const REMEMBERED = 'shiocast.station';

export const useStationsStore = defineStore('stations', () => {
  const stations = ref<StationSummary[]>([]);
  const currentId = ref<string | null>(null);
  const loaded = ref(false);

  const current = computed(
    () => stations.value.find((station) => station.id === currentId.value) ?? null,
  );
  /** The switcher only earns its place in the header when there is a choice to make. */
  const hasChoice = computed(() => stations.value.length > 1);

  function remember(id: string | null): void {
    try {
      if (id) localStorage.setItem(REMEMBERED, id);
      else localStorage.removeItem(REMEMBERED);
    } catch {
      // A private window, or storage the browser refuses. The choice still holds for
      // this page; only remembering it across a reload is lost, which is survivable.
    }
  }

  function recall(): string | null {
    try {
      return localStorage.getItem(REMEMBERED);
    } catch {
      return null;
    }
  }

  function select(id: string): void {
    if (!stations.value.some((station) => station.id === id)) return;
    currentId.value = id;
    setCurrentStation(id);
    remember(id);
  }

  /**
   * Read the stations this account may work in and settle on one.
   *
   * Called after signing in and on a restored session, before the first screen asks the
   * API for anything, so no request goes out without knowing which station it is for.
   */
  async function load(): Promise<void> {
    const response = await request<{ data: StationSummary[] }>('/stations');
    stations.value = response.data;

    const remembered = recall();
    const chosen =
      stations.value.find((station) => station.id === remembered) ?? stations.value[0] ?? null;

    currentId.value = chosen?.id ?? null;
    setCurrentStation(chosen?.id ?? null);
    if (chosen && chosen.id !== remembered) remember(chosen.id);
    loaded.value = true;
  }

  function clear(): void {
    stations.value = [];
    currentId.value = null;
    loaded.value = false;
    setCurrentStation(null);
  }

  return { stations, currentId, current, hasChoice, loaded, load, select, clear };
});
